import { Button, Drawer, Typography } from '@navi/web-ui/lib/primitives';
import { TabItem, Tabs } from '@navi/web-ui/lib/components';
import {
  BULK_UPLOAD_DRAWER_TABS,
  CREATE_NEW,
  NDRA,
  ROLE_OPTIONS,
  UPDATE_EXISTING,
  UPLOAD_NUMBERS,
  UPLOAD_OPTIONS,
  validationOfFile
} from '../interface';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import FileUploader from './fileUploader';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/src/utils/ApiHelper';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import Dropdown from '@cp/src/components/Dropdown';
import { SelectPickerOptionProps } from '@cp/src/components/interfaces';
import { RootState } from '@cp/src/store';
import { Roles } from '../../auth/constants/AuthConstants';
import { TabsChild } from '@navi/web-ui/lib/components/Tabs/types';
import CircularLoaderIcon from '../../Enach/Constants/CircularLoaderIconCopy';

type bulkUploaderDrawerProps = {
  showUploadDrawer: boolean;
  setShowUploadDrawer: React.Dispatch<React.SetStateAction<boolean>>;
};

const BulkUploaderDrawer = ({ showUploadDrawer, setShowUploadDrawer }: bulkUploaderDrawerProps) => {
  const isCreateUserFeatureFlag = useSelector(
    (store: RootState) => store.common.featureFlags?.createUserFeatureFlag
  );
  const isNdraUploadFeatureFlag = useSelector(
    (store: RootState) => store.common.featureFlags?.ndraUploadAccess
  );

  const user = useSelector((state: RootState) => state.common.userData);
  const isNumberManagementRole = user?.roles?.includes(Roles.ROLE_NUMBER_MANAGEMENT);

  const [selectedTab, setSelectedTab] = useState<string>();
  const [createSelectedRole, setCreateSelectedRole] = useState<
    SelectPickerOptionProps | undefined
  >();

  const [createActionType, setCreateActionType] = useState<SelectPickerOptionProps | undefined>();

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);

  const [createUploadFile, setCreateUploadFile] = useState<File | null>(null);
  const [editUploadFile, setEditUploadFile] = useState<File | null>(null);
  const [numberUploadFile, setNumberUploadFile] = useState<File | null>(null);
  const [ndraUploadFile, setNdraUploadFile] = useState<File | null>(null);

  const handleCreateFileUpload = (file: File) => {
    setCreateUploadFile(file);
    setUploadedFile(file);
  };

  const handleNumberFileUpload = (file: File) => {
    setNumberUploadFile(file);
    setUploadedFile(file);
  };

  const handleEditFileUpload = (file: File) => {
    setEditUploadFile(file);
    setUploadedFile(file);
  };

  const handleNdraFileUpload = (file: File) => {
    setNdraUploadFile(file);
    setUploadedFile(file);
  };

  useEffect(() => {
    if (selectedTab) return;
    if (isCreateUserFeatureFlag) {
      setSelectedTab(CREATE_NEW);
      setUploadedFile(createUploadFile);
    } else if (isNumberManagementRole) {
      setSelectedTab(UPLOAD_NUMBERS);
      setUploadedFile(numberUploadFile);
    } else if (isNdraUploadFeatureFlag) {
      setSelectedTab(NDRA);
      setUploadedFile(ndraUploadFile);
    } else {
      setSelectedTab(UPDATE_EXISTING);
      setUploadedFile(editUploadFile);
    }
  }, [isCreateUserFeatureFlag, isNdraUploadFeatureFlag]);

  const handleTabChange = (newTab: string) => {
    setSelectedTab(newTab);
    if (newTab === CREATE_NEW) {
      setUploadedFile(createUploadFile);
    } else if (newTab === UPDATE_EXISTING) {
      setUploadedFile(editUploadFile);
    } else if (newTab === NDRA) {
      setUploadedFile(ndraUploadFile);
    } else {
      setUploadedFile(numberUploadFile);
    }
  };

  const handleUpload = () => {
    if (uploadedFile) {
      setIsUploadDisabled(true);
      if (selectedTab === CREATE_NEW && createUploadFile && createSelectedRole) {
        const selectRole = createSelectedRole.value;
        const url = getApiUrl(ApiKeys.UPLOAD_AGENTS, { selectedRole: selectRole });
        const formData = new FormData();
        formData.append('file', createUploadFile);
        axiosInstance
          .post(url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          .then(response => {
            toast.success('File upload In progress. Please check upload history');
            setShowUploadDrawer(false);
          })
          .catch(error => {
            toast.error(error || 'Failed to add agents.');
          })
          .finally(() => {
            setCreateSelectedRole(null);
            setCreateUploadFile(null);
            setIsUploadDisabled(false);
          });
      } else if (selectedTab === NDRA && ndraUploadFile) {
        const url = getApiUrl(ApiKeys.UPLOAD_NDRA);
        const formData = new FormData();
        formData.append('file', ndraUploadFile);
        axiosInstance
          .post(url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              donotHandleError: true
            }
          })
          .then(response => {
            toast.success('NDRA upload successful');
            setShowUploadDrawer(false);
          })
          .catch(error => {
            const errorMessage = error?.response?.data?.message;
            toast.error(
              errorMessage || (
                <div>
                  Upload failed! Check upload history <br />
                  for more info
                </div>
              )
            );
            setShowUploadDrawer(false);
          })
          .finally(() => {
            setNdraUploadFile(null);
            setIsUploadDisabled(false);
          });
      } else {
        if (editUploadFile) {
          const url = getApiUrl(ApiKeys.UPLOAD_EDIT_AGENTS);
          const formData = new FormData();
          formData.append('file', editUploadFile);
          axiosInstance
            .post(url, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            })
            .then(response => {
              toast.success('File upload In progress. Please check upload history');
              setShowUploadDrawer(false);
            })
            .catch(error => {
              toast.error(error || 'Failed to update agents.');
            })
            .finally(() => {
              setEditUploadFile(null);
              setIsUploadDisabled(false);
            });
        } else {
          if (numberUploadFile && createActionType) {
            const actionType = createActionType.value;

            const url = getApiUrl(ApiKeys.UPLOAD_NUMBER_MANAGEMENT, { action: actionType });

            const formData = new FormData();
            formData.append('file', numberUploadFile);
            axiosInstance
              .post(url, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              })
              .then(response => {
                toast.success('File upload In progress. Please check upload history');
                setShowUploadDrawer(false);
              })
              .catch(error => {
                toast.error(error || 'Failed to update numbers.');
              })
              .finally(() => {
                setNumberUploadFile(null);
                setIsUploadDisabled(false);
              });
          }
        }
      }
      setUploadedFile(null);
    }
  };

  const handleSelectionChange = (selectedRole: SelectPickerOptionProps) => {
    setCreateSelectedRole(selectedRole);
  };

  const handleActionTypeChange = (selectedActionType: SelectPickerOptionProps) => {
    setCreateActionType(selectedActionType);
  };

  const isUploadFileValid = (() => {
    if (selectedTab === UPLOAD_NUMBERS) {
      return createActionType ? !validationOfFile(numberUploadFile) : true;
    }

    if (selectedTab === NDRA) {
      return !validationOfFile(ndraUploadFile);
    }

    if (selectedTab === CREATE_NEW) {
      return createSelectedRole ? !validationOfFile(createUploadFile) : true;
    }

    return !validationOfFile(editUploadFile);
  })();

  const getFileUploadHandler = () => {
    if (selectedTab === UPLOAD_NUMBERS) {
      return handleNumberFileUpload;
    } else if (selectedTab === CREATE_NEW) {
      return handleCreateFileUpload;
    } else if (selectedTab === NDRA) {
      return handleNdraFileUpload;
    } else {
      return handleEditFileUpload;
    }
  };

  const getUploadedFileHandler = () => {
    if (selectedTab === UPLOAD_NUMBERS) {
      return numberUploadFile;
    } else if (selectedTab === CREATE_NEW) {
      return createUploadFile;
    } else if (selectedTab === NDRA) {
      return ndraUploadFile;
    } else {
      return editUploadFile;
    }
  };

  const setUploadedFileHandler = () => {
    if (selectedTab === UPLOAD_NUMBERS) {
      return setNumberUploadFile;
    } else if (selectedTab === CREATE_NEW) {
      return setCreateUploadFile;
    } else if (selectedTab === NDRA) {
      return setNdraUploadFile;
    } else {
      return setEditUploadFile;
    }
  };

  const getDefaultSelectedKey = () => {
    if (isCreateUserFeatureFlag) {
      return CREATE_NEW;
    } else if (isNumberManagementRole) {
      return UPLOAD_NUMBERS;
    } else if (isNdraUploadFeatureFlag) {
      return NDRA;
    } else {
      return UPDATE_EXISTING;
    }
  };

  return (
    <Drawer
      headerText={'Bulk upload'}
      open={showUploadDrawer}
      usePadding={false}
      onClose={() => {
        setShowUploadDrawer(false);
      }}
      showHeaderDivider={true}
      style={{ width: '500px' }}
      footer={
        <Button
          variant="primary"
          className="py-[8px] w-[100%]"
          onClick={handleUpload}
          disabled={isUploadFileValid || isUploadDisabled}
        >
          Upload
        </Button>
      }
    >
      <div className="w-[100%]">
        <Tabs
          defaultSelectedKey={getDefaultSelectedKey()}
          orientation="horizontal"
          onTabChange={newTab => handleTabChange(newTab)}
          tabsClassName="w-[100%] px-[16px] pt-[12px] gap-[8px]"
        >
          {BULK_UPLOAD_DRAWER_TABS.reduce<TabsChild[]>((acc, item) => {
            if (item.key === CREATE_NEW && !isCreateUserFeatureFlag) {
              return acc;
            }
            if (item.key === UPLOAD_NUMBERS && !isNumberManagementRole) {
              return acc;
            }
            if (item.key === NDRA && !isNdraUploadFeatureFlag) {
              return acc;
            }
            {
              acc.push(
                <TabItem key={item.key} label={item.value}>
                  {selectedTab === item.key ? (
                    <div className="px-[16px] pt-[24px]">
                      {selectedTab === CREATE_NEW ? (
                        <>
                          <Typography
                            variant={'p5'}
                            className={'text-grayscale-2 text-sm font-normal'}
                          >
                            Select role type
                          </Typography>
                          <Dropdown
                            key={String(showUploadDrawer)}
                            options={ROLE_OPTIONS}
                            defaultSelected={createSelectedRole}
                            onSelectionChange={e => {
                              handleSelectionChange(e);
                            }}
                            placeholder="Select here"
                            arrowIconWidth={20}
                            containerClasses="mb-[24px] mt-[8px] w-[100%]"
                          />
                        </>
                      ) : null}
                      {selectedTab === UPLOAD_NUMBERS ? (
                        <>
                          <Typography
                            variant={'p5'}
                            className={'text-grayscale-2 text-sm font-normal'}
                          >
                            Select action type
                          </Typography>
                          <Dropdown
                            key={String(showUploadDrawer)}
                            options={UPLOAD_OPTIONS}
                            defaultSelected={createActionType}
                            onSelectionChange={e => {
                              handleActionTypeChange(e);
                            }}
                            placeholder="Select here"
                            arrowIconWidth={20}
                            containerClasses="mb-[24px] mt-[8px] w-[100%]"
                          />
                        </>
                      ) : null}
                      <Typography variant="p5" className="text-grayscale-2 pb-1">
                        Upload a CSV to{' '}
                        {selectedTab === NDRA
                          ? 'generate NDRA certificates'
                          : 'update user details'}
                      </Typography>
                      <FileUploader
                        key={item.key}
                        selectedTab={selectedTab}
                        onFileUpload={getFileUploadHandler()}
                        uploadedFile={getUploadedFileHandler()}
                        setUploadedFile={setUploadedFileHandler()}
                      />
                      {isUploadDisabled ? (
                        <div className="flex items-center gap-1 mt-4">
                          <CircularLoaderIcon fill="var(--navi-color-gray-c3)" />
                          <Typography variant="h5" color="var(--navi-color-gray-c3)">
                            Validating...
                          </Typography>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </TabItem>
              );
            }
            return acc;
          }, [])}
        </Tabs>
      </div>
    </Drawer>
  );
};

export default BulkUploaderDrawer;
